import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 响应式应用外壳：一套页面组件，两种壳。
 *
 * 核心约定（来自踩坑手册 §6）：
 *
 * > **桌面版布局不要重写页面。** 桌面壳里放的就是手机端那几个页面组件——
 * > 重写一套桌面页面意味着此后每个功能都要改两遍，而且两边迟早不一致。
 *
 * > **桌面样式整份关在作用域类里（`.ps-desk`），桌面壳不渲染就等于不存在。**
 * > 这比"用媒体查询覆盖既有样式"安全得多——后者迟早有一条漏到手机上，
 * > 而漏下去的那条只在某个尺寸区间才显形，很难复现。
 *
 * 所以这里是**换壳**，不是加媒体查询：
 * - 窄屏 → `<Screen>` + 底部 `<TabBar>`（原样，一行没动）
 * - 宽屏 → 左侧 `<SideNav>` + 可选列表栏 + 内容栏
 *
 * ⚠️ 判据是**窗口宽度**，不是 UA。iPad 横屏、桌面把窗口拖窄、分屏——UA 全都判不出来，
 * 而且用户可以在使用中随时改变它。
 */
import { useCallback, useEffect } from 'react';
import { useIsDesktop, useTitlebarArea } from '@pwaship/react';
import { Screen } from './primitives.js';
import { TabBar } from './navigation.js';
/**
 * 桌面侧边导航。收的是**和 TabBar 完全一样的 items**——
 * 导航配置只有一份，加一个页面不需要在两个地方各改一遍。
 */
export function SideNav({ items, active, onChange, brand, footer, compact = false, className = '' }) {
    const { overlayVisible } = useTitlebarArea();
    return (_jsxs("nav", { className: `ps-side-nav ${compact ? 'is-compact' : ''} ${className}`.trim(), "aria-label": "\u4E3B\u5BFC\u822A", "data-ps-wco": overlayVisible ? '' : undefined, children: [brand && (
            // 标题栏接管生效时，这一条同时是窗口拖拽区（见 components.css 里的 app-region）
            _jsx("div", { className: "ps-side-brand", children: brand })), _jsx("div", { className: "ps-side-items", role: "tablist", "aria-orientation": "vertical", children: items.map((item) => {
                    const isActive = item.key === active;
                    return (_jsxs("button", { type: "button", role: "tab", "aria-selected": isActive, "aria-label": compact ? item.label : undefined, title: compact ? item.label : undefined, "data-ps-pressable": "", className: `ps-side-item ${isActive ? 'is-active' : ''}`.trim(), onClick: () => onChange(item.key), children: [_jsxs("span", { className: "ps-side-icon", children: [isActive ? (item.activeIcon ?? item.icon) : item.icon, item.dot && !item.badge ? _jsx("span", { className: "ps-tab-dot", "aria-hidden": "true" }) : null, item.badge ? (_jsx("span", { className: "ps-tab-badge", "aria-label": `${item.badge} 条未读`, children: item.badge > 99 ? '99+' : item.badge })) : null] }), !compact && _jsx("span", { className: "ps-side-label", children: item.label })] }, item.key));
                }) }), footer && _jsx("div", { className: "ps-side-footer", children: footer })] }));
}
/**
 * 一个 `<AppShell>` 同时管两种形态。业务页面组件在两边是同一份。
 *
 * ```tsx
 * <AppShell items={TABS} active={tab} onChange={setTab} brand={<Logo />} aside={<ChatList />}>
 *   <ChatView id={id} />
 * </AppShell>
 * ```
 */
export function AppShell({ items, active, onChange, brand, navFooter, compactNav = false, aside, header, footer, desktopMinWidth = 1024, className = '', children, }) {
    const isDesktop = useIsDesktop(desktopMinWidth);
    const { overlayVisible, height: titlebarHeight } = useTitlebarArea();
    const handleChange = useCallback((key) => onChange(key), [onChange]);
    /*
     * 把当前形态挂到 <html> 上。
     *
     * 为什么需要它：fixed 定位的层（toast、遮盖层）不在 .ps-desk 里面，作用域类够不着；
     * 而给它们写 `@media (min-width: 1024px)` 等于把断点抄了第二份——
     * 有人把 desktopMinWidth 调成 1280 之后，1100px 宽就会得到"移动壳 + 桌面 toast"。
     * 形态只有一个来源，就是这里。
     */
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        const root = document.documentElement;
        root.setAttribute('data-ps-shell', isDesktop ? 'desktop' : 'mobile');
        return () => root.removeAttribute('data-ps-shell');
    }, [isDesktop]);
    if (!isDesktop) {
        // 窄屏：原样走移动端那一套，一行没动
        return (_jsxs("div", { className: `ps-app ${className}`.trim(), children: [_jsx(Screen, { header: header, footer: footer, children: children }), _jsx(TabBar, { items: items, active: active, onChange: handleChange })] }));
    }
    return (_jsxs("div", { className: `ps-app ps-desk ${aside ? 'has-aside' : ''} ${className}`.trim(), 
        // 标题栏被应用接管时，把系统给的高度传下去；没接管时是 0，CSS 里的 max() 会兜住
        style: overlayVisible ? { '--ps-titlebar-h': `${titlebarHeight}px` } : undefined, children: [_jsx(SideNav, { items: items, active: active, onChange: handleChange, ...(brand ? { brand } : {}), ...(navFooter ? { footer: navFooter } : {}), compact: compactNav }), aside && _jsx("div", { className: "ps-desk-aside ps-scroll", children: aside }), _jsxs("main", { className: "ps-desk-main", children: [header && _jsx("div", { className: "ps-desk-header", children: header }), _jsx("div", { className: "ps-desk-body ps-scroll", children: children }), footer && _jsx("div", { className: "ps-desk-footer", children: footer })] })] }));
}
//# sourceMappingURL=app-shell.js.map